'use client';

/**
 * Glassy mobile-app showcase panel — frosted-glass card with a
 * contained phone hero, accent halo, optional chat bubble overlay.
 * Replaces an earlier "phone breaks the dark band" design that
 * required fragile measurement coupling.
 *
 * <ul>
 *   <li>Panel: translucent card surface with backdrop blur, dot-grid
 *       texture overlay (masked to fade at edges), accent-tinted
 *       border at low alpha.</li>
 *   <li>Phone: centered in its column, 9:19 aspect, dark frame, notch
 *       overlay, designed in-screen placeholder when no image.</li>
 *   <li>Halo: radial accent glow behind the phone.</li>
 *   <li>Chat bubble: single floating bubble overlapping the phone's
 *       lower portion as proof-of-product.</li>
 *   <li>Stores: dual CTAs (Apple / Google / Huawei glyphs) — primary
 *       solid + secondary ghost.</li>
 * </ul>
 *
 * <p>Ported from {@code ThemeMaker/lms-portal/src/components/blocks/
 * theme/MobileAppShowcase/MobileAppShowcase}.
 */
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { tx, asList } from '../_shared/coerce';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

type AccentTone = 'brand' | 'accent' | 'success' | 'amber';

interface ShowcaseStat {
  value?: Localized;
  label?: Localized;
}

interface ChatBubble {
  text?: Localized;
  avatarImage?: string;
  avatarInitials?: Localized;
}

interface StorePlatform {
  label?: Localized;
  caption?: Localized;
  href?: string;
  platform?: 'apple' | 'google' | 'huawei';
}

export interface MobileAppShowcaseProps {
  kicker?: Localized;
  title?: Localized;
  highlight?: Localized;
  body?: Localized;
  stats?: ShowcaseStat[];
  phoneImage?: string;
  phoneImageAlt?: Localized;
  chatBubble?: ChatBubble | null;
  primaryStore?: StorePlatform;
  secondaryStore?: StorePlatform;
  accent?: AccentTone;
  imagePosition?: 'left' | 'right';
  style?: CSSProperties;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

/** Stock proof stats and chat bubble, used until the author supplies
 *  their own. Built per render so a language switch re-reads them. */
const defaultStats = (t: (key: string) => string): ShowcaseStat[] => [
  { value: '450+', label: t('mobileAppShowcase.stat.lessons') },
  { value: t('mobileAppShowcase.stat.hoursValue'), label: t('mobileAppShowcase.stat.content') },
  { value: '∞', label: t('mobileAppShowcase.stat.replay') },
];

const defaultChatBubble = (t: (key: string) => string): ChatBubble => ({
  text: t('mobileAppShowcase.bubble.text'),
  avatarInitials: 'AY',
});

const ACCENT_TOKENS: Record<AccentTone, string> = {
  brand: 'var(--color-brand-500, #3b82f6)',
  accent: 'var(--color-accent-500, #8b5cf6)',
  success: 'var(--color-feedback-success, #10b981)',
  amber: 'var(--color-amber-500, #f59e0b)',
};

type ShowcaseVars = CSSProperties & {
  '--showcase-accent': string;
};

export function MobileAppShowcase({
  kicker,
  title,
  highlight,
  body,
  stats,
  phoneImage,
  phoneImageAlt,
  chatBubble,
  primaryStore,
  secondaryStore,
  accent = 'brand',
  imagePosition = 'right',
  style,
  paddingOuter,
}: MobileAppShowcaseProps) {
  const { t } = useTranslation('site-builder-blocks');
  const kickerText = kicker ?? t('mobileAppShowcase.kicker');
  const titleText = title ?? t('mobileAppShowcase.title');
  const bodyText = body ?? t('mobileAppShowcase.body');
  const phoneAltText = phoneImageAlt ?? t('mobileAppShowcase.phoneAlt');
  // `chatBubble={null}` is the author's way of switching the bubble off,
  // so only an absent prop falls back to the stock one.
  const bubble = chatBubble === undefined ? defaultChatBubble(t) : chatBubble;
  const primary = primaryStore ?? {
    label: t('mobileAppShowcase.store.appStore'),
    caption: t('mobileAppShowcase.store.appStoreSub'),
    platform: 'apple' as const,
    href: '#',
  };
  const secondary = secondaryStore ?? {
    label: t('mobileAppShowcase.store.playStore'),
    caption: t('mobileAppShowcase.store.playStoreSub'),
    platform: 'google' as const,
    href: '#',
  };
  const statList = asList<ShowcaseStat>(stats ?? defaultStats(t)).slice(0, 3);
  const accentVar = ACCENT_TOKENS[accent] ?? ACCENT_TOKENS.brand;
  const shellStyle: ShowcaseVars = { '--showcase-accent': accentVar };

  const hasBubble = !!(
    bubble &&
    (tx(bubble.text) || bubble.avatarImage || tx(bubble.avatarInitials))
  );

  return (
    <BlockRoot
      as="section"
      className={`showcase3-section showcase3-image-${imagePosition}`}
      style={{ ...shellStyle, ...resolvePaddingStyle(paddingOuter), ...style }}
      aria-labelledby="showcase3-title"
    >
      <style dangerouslySetInnerHTML={{ __html: CSS }} />

      <div className="showcase3-panel">
        <div className="showcase3-texture" aria-hidden />

        <div className="showcase3-inner">
          <div className="showcase3-copy">
            {kickerText && <div className="showcase3-kicker">{tx(kickerText)}</div>}

            <h2 id="showcase3-title" className="showcase3-title">
              {tx(titleText)}
              {highlight && (
                <>
                  {' '}
                  <span className="showcase3-title-highlight">{tx(highlight)}</span>
                </>
              )}
            </h2>

            {bodyText && <p className="showcase3-body">{tx(bodyText)}</p>}

            {statList.length > 0 && (
              <div className="showcase3-stats" role="list">
                {statList.map((s, i) => (
                  <div key={i} className="showcase3-stat" role="listitem">
                    <span className="showcase3-stat-value">{tx(s.value)}</span>
                    {s.label && <span className="showcase3-stat-label">{tx(s.label)}</span>}
                  </div>
                ))}
              </div>
            )}

            <div className="showcase3-ctas">
              {primary?.href && (
                <a
                  className="showcase3-cta showcase3-cta-primary"
                  href={String(primary.href)}
                  aria-label={`${tx(primary.caption)} ${tx(primary.label)}`}
                >
                  <StoreGlyph platform={primary.platform ?? 'apple'} />
                  <span className="showcase3-cta-text">
                    <span className="showcase3-cta-caption">{tx(primary.caption)}</span>
                    <span className="showcase3-cta-label">{tx(primary.label)}</span>
                  </span>
                </a>
              )}
              {secondary?.href && (
                <a
                  className="showcase3-cta showcase3-cta-ghost"
                  href={String(secondary.href)}
                  aria-label={`${tx(secondary.caption)} ${tx(secondary.label)}`}
                >
                  <StoreGlyph platform={secondary.platform ?? 'google'} />
                  <span className="showcase3-cta-text">
                    <span className="showcase3-cta-caption">{tx(secondary.caption)}</span>
                    <span className="showcase3-cta-label">{tx(secondary.label)}</span>
                  </span>
                </a>
              )}
            </div>
          </div>

          <div className="showcase3-visual">
            <div className="showcase3-halo" aria-hidden />

            <div className="showcase3-phone-frame">
              {phoneImage ? (
                <img
                  src={phoneImage}
                  alt={tx(phoneAltText) || t('mobileAppShowcase.phoneAlt')}
                  className="showcase3-phone-screen"
                  loading="lazy"
                />
              ) : (
                <div className="showcase3-phone-placeholder" aria-hidden>
                  <div className="showcase3-phone-placeholder-statusbar">
                    <span>9:41</span>
                    <span className="showcase3-phone-placeholder-icons">●●●</span>
                  </div>
                  <div className="showcase3-phone-placeholder-greeting">
                    {t('mobileAppShowcase.screen.greeting')}
                  </div>
                  <div className="showcase3-phone-placeholder-heading">
                    {t('mobileAppShowcase.screen.prompt')}
                  </div>
                  <div className="showcase3-phone-placeholder-course">
                    <div className="showcase3-phone-placeholder-course-thumb" />
                    <div className="showcase3-phone-placeholder-course-meta">
                      <div className="showcase3-phone-placeholder-course-title">
                        {t('mobileAppShowcase.screen.courseTitle')}
                      </div>
                      <div className="showcase3-phone-placeholder-course-sub">
                        {t('mobileAppShowcase.screen.courseMeta')}
                      </div>
                      <div className="showcase3-phone-placeholder-progress">
                        <div className="showcase3-phone-placeholder-progress-bar" />
                      </div>
                    </div>
                  </div>
                  <div className="showcase3-phone-placeholder-section">
                    {t('mobileAppShowcase.screen.recommended')}
                  </div>
                  <div className="showcase3-phone-placeholder-cards">
                    <div className="showcase3-phone-placeholder-card-mini" />
                    <div className="showcase3-phone-placeholder-card-mini showcase3-phone-placeholder-card-mini-alt" />
                  </div>
                </div>
              )}

              <div className="showcase3-phone-notch" aria-hidden />
            </div>

            {hasBubble && bubble && (
              <div className="showcase3-bubble" role="note">
                <div className="showcase3-bubble-avatar">
                  {bubble.avatarImage ? (
                    <img
                      src={bubble.avatarImage}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      width={40}
                      height={40}
                    />
                  ) : (
                    <span aria-hidden>{tx(bubble.avatarInitials) || '·'}</span>
                  )}
                </div>
                {tx(bubble.text) && <div className="showcase3-bubble-text">{tx(bubble.text)}</div>}
              </div>
            )}
          </div>
        </div>
      </div>
    </BlockRoot>
  );
}

function StoreGlyph({ platform }: { platform: 'apple' | 'google' | 'huawei' }) {
  if (platform === 'apple') {
    return (
      <svg
        viewBox="0 0 24 24"
        width="22"
        height="22"
        aria-hidden
        focusable="false"
        fill="currentColor"
      >
        <path d="M16.498 12.78c-.022-2.34 1.91-3.46 1.998-3.515-1.087-1.587-2.776-1.804-3.376-1.83-1.435-.146-2.806.844-3.535.844-.74 0-1.86-.823-3.06-.8-1.572.024-3.024.913-3.83 2.318-1.642 2.835-.418 7.024 1.182 9.32.78 1.124 1.708 2.382 2.92 2.337 1.179-.047 1.62-.757 3.04-.757 1.41 0 1.81.757 3.05.732 1.262-.022 2.062-1.142 2.83-2.273.892-1.302 1.258-2.575 1.279-2.642-.028-.012-2.45-.94-2.475-3.732zM14.7 5.84c.65-.787 1.087-1.88.969-2.967-.937.037-2.07.624-2.74 1.408-.6.694-1.124 1.806-.985 2.872 1.044.082 2.108-.532 2.756-1.313z" />
      </svg>
    );
  }
  if (platform === 'google') {
    return (
      <svg
        viewBox="0 0 24 24"
        width="22"
        height="22"
        aria-hidden
        focusable="false"
        fill="currentColor"
      >
        <path d="M3.6 1.6c-.4.3-.6.8-.6 1.4v18c0 .6.2 1.1.6 1.4L13.7 12 3.6 1.6zm11.5 11.7l2.7 2.7-12.2 7c-.3.2-.6.2-.9.1l10.4-9.8zm5.4-3.2l-3 1.7-2.8 2.8 2.8 2.8 3-1.7c1.1-.7 1.1-2.1 0-2.8l0-2.8zm-7.7-.9L4.4 1.5c.3-.1.6-.1.9.1l12.2 7-2.7 2.6z" />
      </svg>
    );
  }
  return (
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      aria-hidden
      focusable="false"
      fill="currentColor"
    >
      <circle cx="12" cy="12" r="10" />
    </svg>
  );
}

const CSS = `
.showcase3-section {
  position: relative;
  padding: var(--space-16, 64px) var(--space-6, 24px);
  background: transparent;
}
.showcase3-panel {
  position: relative;
  max-width: var(--sb-max-width-1200, var(--app-max-width, 1200px)); width: 100%; margin: 0 auto;
  border-radius: clamp(16px, 2vw, 28px);
  background: color-mix(in srgb, var(--color-surface-card, #ffffff) 60%, transparent);
  backdrop-filter: blur(20px) saturate(140%);
  -webkit-backdrop-filter: blur(20px) saturate(140%);
  border: 1px solid color-mix(in srgb, var(--showcase-accent) 18%, transparent);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 30px 60px -30px rgba(0, 0, 0, 0.25),
    0 12px 24px -16px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  isolation: isolate;
}
.showcase3-texture {
  position: absolute; inset: 0;
  background-image: radial-gradient(circle,
    color-mix(in srgb, var(--color-text-primary, #0f1218) 18%, transparent) 1px,
    transparent 1.5px);
  background-size: 24px 24px;
  mask-image: radial-gradient(ellipse 80% 70% at 50% 50%,
    rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0) 100%);
  -webkit-mask-image: radial-gradient(ellipse 80% 70% at 50% 50%,
    rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 60%, rgba(0,0,0,0) 100%);
  pointer-events: none;
  z-index: 0;
}
.showcase3-inner {
  position: relative; z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(280px, 0.9fr);
  align-items: center;
  gap: clamp(24px, 5vw, 72px);
  padding: clamp(32px, 5vw, 64px) clamp(24px, 4vw, 56px);
}
.showcase3-image-left .showcase3-inner {
  grid-template-columns: minmax(280px, 0.9fr) minmax(0, 1.1fr);
}
.showcase3-image-left .showcase3-copy   { order: 2; }
.showcase3-image-left .showcase3-visual { order: 1; }
@media (max-width: 959px) {
  .showcase3-inner,
  .showcase3-image-left .showcase3-inner { grid-template-columns: 1fr; }
  .showcase3-copy { order: 1; }
  .showcase3-visual { order: 2; }
}
.showcase3-kicker {
  display: inline-block;
  font-family: var(--font-family-mono, ui-monospace, monospace);
  font-size: var(--font-size-xs, 12px);
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--showcase-accent);
  font-weight: 600;
  margin-bottom: clamp(16px, 2vw, 24px);
  padding: 4px 10px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--showcase-accent) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--showcase-accent) 25%, transparent);
}
.showcase3-title {
  margin: 0;
  color: var(--color-text-primary, #0f1218);
  font-family: var(--font-family-display, inherit);
  font-size: clamp(32px, 4.5vw, 56px);
  font-weight: 700;
  letter-spacing: -0.025em;
  line-height: 1.05;
  max-width: 14ch;
}
.showcase3-title-highlight {
  color: var(--showcase-accent);
  font-weight: 700;
}
.showcase3-body {
  margin: clamp(16px, 2vw, 24px) 0 0;
  max-width: 480px;
  color: var(--color-text-secondary, #4a5568);
  font-family: var(--font-family-body, inherit);
  font-size: var(--font-size-md, 16px);
  line-height: 1.6;
}
.showcase3-stats {
  display: flex; flex-wrap: wrap;
  gap: clamp(20px, 3vw, 32px);
  margin: clamp(20px, 3vw, 28px) 0;
  padding: clamp(16px, 2vw, 20px) 0 0;
  border-top: 1px solid var(--color-border-subtle, rgba(120, 120, 140, 0.18));
}
.showcase3-stat { display: flex; align-items: baseline; gap: 8px; font-family: var(--font-family-body, inherit); }
.showcase3-stat-value {
  font-family: var(--font-family-display, inherit);
  font-size: var(--font-size-xl, 22px);
  font-weight: 700;
  color: var(--color-text-primary, #0f1218);
  letter-spacing: -0.01em; line-height: 1;
}
.showcase3-stat-label {
  font-size: var(--font-size-xs, 12px);
  color: var(--color-text-secondary, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 500;
}
.showcase3-ctas {
  display: flex; flex-wrap: wrap;
  gap: 12px;
  margin-top: clamp(20px, 3vw, 28px);
}
.showcase3-cta {
  display: inline-flex; align-items: center;
  gap: 12px;
  padding: 12px 18px;
  border-radius: 12px;
  text-decoration: none;
  font-family: var(--font-family-body, inherit);
  transition: transform 200ms ease, box-shadow 200ms ease, background 200ms ease;
}
.showcase3-cta:hover { transform: translateY(-1px); }
.showcase3-cta-primary {
  background: var(--showcase-accent);
  color: var(--color-text-on-brand, #ffffff);
  box-shadow:
    0 12px 24px -8px color-mix(in srgb, var(--showcase-accent) 50%, transparent),
    0 1px 0 rgba(255, 255, 255, 0.2) inset;
}
.showcase3-cta-primary:hover {
  box-shadow:
    0 16px 32px -8px color-mix(in srgb, var(--showcase-accent) 60%, transparent),
    0 1px 0 rgba(255, 255, 255, 0.2) inset;
}
.showcase3-cta-ghost {
  background: color-mix(in srgb, var(--color-text-primary, #0f1218) 6%, transparent);
  color: var(--color-text-primary, #0f1218);
  border: 1px solid var(--color-border-subtle, rgba(120, 120, 140, 0.25));
}
.showcase3-cta-ghost:hover {
  background: color-mix(in srgb, var(--color-text-primary, #0f1218) 10%, transparent);
}
.showcase3-cta-text {
  display: flex; flex-direction: column;
  gap: 1px;
  align-items: flex-start;
  line-height: 1.1;
}
.showcase3-cta-caption {
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.8;
}
.showcase3-cta-label {
  font-size: var(--font-size-sm, 14px);
  font-weight: 700;
}
.showcase3-visual {
  position: relative;
  display: flex; align-items: center; justify-content: center;
  min-height: 460px;
}
.showcase3-halo {
  position: absolute;
  inset: -10% -15% -10% -15%;
  z-index: 0;
  background: radial-gradient(ellipse at 50% 50%,
    color-mix(in srgb, var(--showcase-accent) 35%, transparent) 0%,
    color-mix(in srgb, var(--showcase-accent) 15%, transparent) 35%,
    transparent 70%);
  filter: blur(12px);
  pointer-events: none;
}
.showcase3-phone-frame {
  position: relative; z-index: 1;
  width: clamp(220px, 22vw, 280px);
  aspect-ratio: 9 / 19;
  border-radius: clamp(28px, 3.5vw, 42px);
  background: linear-gradient(160deg, #2a2f3a 0%, #0a0d14 60%);
  padding: 8px;
  box-shadow:
    0 40px 80px -20px rgba(0, 0, 0, 0.5),
    0 20px 40px -10px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.08) inset,
    0 1px 0 rgba(255, 255, 255, 0.15) inset;
}
.showcase3-phone-screen {
  display: block;
  width: 100%; height: 100%;
  object-fit: cover;
  border-radius: clamp(22px, 2.8vw, 36px);
  background: #fff;
}
.showcase3-phone-notch {
  position: absolute;
  top: 14px; left: 50%;
  transform: translateX(-50%);
  width: 78px; height: 22px;
  border-radius: 11px;
  background: #000;
  z-index: 2;
}
.showcase3-phone-placeholder {
  width: 100%; height: 100%;
  border-radius: clamp(22px, 2.8vw, 36px);
  background: linear-gradient(180deg, #ffffff 0%, #f5f5f8 100%);
  padding: 38px 16px 16px;
  display: flex; flex-direction: column; gap: 10px;
  font-family: var(--font-family-body, -apple-system, system-ui, sans-serif);
  color: #0f1218;
  overflow: hidden;
}
.showcase3-phone-placeholder-statusbar {
  display: flex; justify-content: space-between; align-items: center;
  font-size: 10px; font-weight: 600;
  margin-bottom: 4px;
  padding: 0 6px;
}
.showcase3-phone-placeholder-icons {
  font-size: 6px; letter-spacing: 1px; opacity: 0.7;
}
.showcase3-phone-placeholder-greeting {
  font-size: 11px; color: #6b7280;
  font-weight: 500;
  margin-top: 6px;
}
.showcase3-phone-placeholder-heading {
  font-size: 15px; font-weight: 700;
  letter-spacing: -0.01em; line-height: 1.2;
  margin-bottom: 6px;
}
.showcase3-phone-placeholder-course {
  display: flex; gap: 10px;
  padding: 10px;
  border-radius: 12px;
  background: linear-gradient(135deg,
    color-mix(in srgb, var(--showcase-accent) 14%, white),
    color-mix(in srgb, var(--showcase-accent) 4%, white));
  border: 1px solid color-mix(in srgb, var(--showcase-accent) 22%, transparent);
  margin-bottom: 4px;
}
.showcase3-phone-placeholder-course-thumb {
  flex: 0 0 44px;
  width: 44px; height: 44px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--showcase-accent),
    color-mix(in srgb, var(--showcase-accent) 60%, #000));
  position: relative;
}
.showcase3-phone-placeholder-course-thumb::after {
  content: '';
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-40%, -50%);
  width: 0; height: 0;
  border-left: 7px solid white;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
}
.showcase3-phone-placeholder-course-meta {
  flex: 1;
  display: flex; flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.showcase3-phone-placeholder-course-title {
  font-size: 11px; font-weight: 600;
  white-space: nowrap;
  overflow: hidden; text-overflow: ellipsis;
}
.showcase3-phone-placeholder-course-sub {
  font-size: 9px; color: #6b7280;
  margin-bottom: 4px;
}
.showcase3-phone-placeholder-progress {
  height: 3px; border-radius: 2px;
  background: rgba(0, 0, 0, 0.08);
  overflow: hidden;
}
.showcase3-phone-placeholder-progress-bar {
  height: 100%; width: 65%;
  background: var(--showcase-accent);
}
.showcase3-phone-placeholder-section {
  font-size: 10px; font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 6px;
}
.showcase3-phone-placeholder-cards {
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.showcase3-phone-placeholder-card-mini {
  height: 70px; border-radius: 10px;
  background: linear-gradient(135deg, #e2e8f0, #cbd5e1);
}
.showcase3-phone-placeholder-card-mini-alt {
  background: linear-gradient(135deg,
    color-mix(in srgb, var(--showcase-accent) 18%, white),
    color-mix(in srgb, var(--showcase-accent) 6%, white));
}
.showcase3-bubble {
  position: absolute;
  bottom: 12%; left: 8%;
  z-index: 3;
  display: flex; align-items: center;
  gap: 10px;
  padding: 8px 14px 8px 8px;
  border-radius: 999px;
  background: var(--color-surface-card, #ffffff);
  color: var(--color-text-primary, #0f1218);
  box-shadow:
    0 14px 32px -8px rgba(0, 0, 0, 0.3),
    0 1px 0 rgba(255, 255, 255, 0.5) inset;
  max-width: 240px;
  font-family: var(--font-family-body, inherit);
  border: 1px solid var(--color-border-subtle, rgba(120, 120, 140, 0.18));
}
.showcase3-image-left .showcase3-bubble { left: auto; right: 8%; }
@media (max-width: 959px) {
  .showcase3-bubble {
    bottom: 8%; left: 50%;
    transform: translateX(-50%);
    right: auto;
  }
  .showcase3-image-left .showcase3-bubble { right: auto; }
}
.showcase3-bubble-avatar {
  flex: 0 0 36px;
  width: 36px; height: 36px;
  border-radius: 50%;
  overflow: hidden;
  background: linear-gradient(135deg, var(--showcase-accent),
    color-mix(in srgb, var(--showcase-accent) 60%, #000));
  display: grid; place-items: center;
  color: #fff;
  font-weight: 700;
  font-size: var(--font-size-xs, 12px);
}
.showcase3-bubble-avatar img {
  display: block;
  width: 100%; height: 100%;
  object-fit: cover;
}
.showcase3-bubble-text {
  font-size: var(--font-size-sm, 14px);
  font-weight: 600;
  line-height: 1.3;
}
`;
